import { useState } from "react";
import styled from "styled-components";
import type { CountryTaxConfig } from "@/api/countryTaxConfig";
import { FormField } from "@/shared/components";
import { mediaPhone } from "@/shared/theme/breakpoints";
import { formatRatePercent } from "@/shared/utils/percent";
import type { TaxDetailsErrors, TaxDetailsValues } from "@/features/creator-tax";
import { CreatorPaymentTermsModal } from "./CreatorPaymentTermsModal";
import { pressedTint } from "@/shared/theme/pressed";
import { PAYMENT_TERMS_NAME } from "@/shared/legal/administrationFee";

interface Props {
	country: string;
	countryName: string;
	tax: TaxDetailsValues;
	errors: TaxDetailsErrors;
	config: CountryTaxConfig | null;
	existingNumberLast4: string | null;
	taxDirty: boolean;
	taxRegistrationChoice: boolean | null;
	agreed?: boolean;
	showAgreement?: boolean;
	onRegistrationChange: (registered: boolean) => void;
	onTaxChange: <K extends keyof TaxDetailsValues>(field: K, value: TaxDetailsValues[K]) => void;
	onAgreementChange?: (agreed: boolean) => void;
}

const RequiredMark = styled.span`
	color: #d80027;
`;
const Field = styled(FormField)`
	min-width: 0;
`;
const TaxQuestion = styled.p<{ $strong?: boolean }>`
	margin: 0 0 10px;
	color: #303030;
	font-size: 14px;
	font-weight: ${({ $strong }) => ($strong ? 600 : 400)};
	line-height: 24px;
	strong {
		font-weight: 600;
	}
`;
const TaxChoices = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 9px;
	margin-bottom: 24px;
`;
const TaxChoice = styled.button<{ $selected: boolean }>`
	min-height: 50px;
	padding: 12px 16px;
	border: ${({ $selected }) => ($selected ? "2px solid #4861ff" : "1px solid #cdd4f6")};
	border-radius: 8px;
	background: #fff;
	color: ${({ $selected }) => ($selected ? "#3a0ca3" : "#303030")};
	font: inherit;
	font-size: 14px;
	font-weight: 600;
	text-align: left;
	cursor: pointer;
	&::before {
		content: "";
		display: inline-block;
		width: 18px;
		height: 18px;
		margin-right: 12px;
		vertical-align: -4px;
		border: 1.5px solid ${({ $selected }) => ($selected ? "#4861ff" : "#aaa")};
		border-radius: 50%;
		background: ${({ $selected }) => ($selected ? "radial-gradient(circle, white 0 3px, #4861ff 4px)" : "white")};
	}

	${pressedTint}
`;
const TaxFields = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1.8fr) minmax(0, 1fr);
	gap: 9px;
	margin-bottom: 24px;

	${mediaPhone`
		grid-template-columns: minmax(0, 1fr);
		gap: 16px;
	`}
`;
const TaxNotice = styled.p`
	margin: 0;
	padding: 12px 14px;
	border: 1px solid #ffdc9d;
	border-radius: 8px;
	background: #fff8e9;
	color: #c87a00;
	font-size: 12px;
	line-height: 18px;
`;
const TaxError = styled.p`
	color: #d92d20;
	font-size: 12px;
	margin: -16px 0 16px;
`;
const TermsAgreement = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 8px;
	margin-top: 24px;
	color: #303030;
	font-size: 14px;
	line-height: 24px;
	cursor: pointer;
	input {
		width: 24px;
		height: 24px;
		margin: 0;
		accent-color: #3a0ca3;
	}
	button {
		padding: 0;
		border: 0;
		background: transparent;
		color: #3a0ca3;
		font: inherit;
		font-weight: 600;
		text-align: left;
		text-decoration: underline;
		white-space: normal;
		cursor: pointer;
	}
`;

const Input = styled.input`
	width: 100%;
	height: 40px;
	padding: 8px 12px;
	border: 1px solid #cdd4f6;
	border-radius: 8px;
	outline: none;
	background: #fff;
	color: #171717;
	font: inherit;
	font-size: 16px;

	&:focus {
		border-color: #9fadf1;
	}
`;

const Heading = styled.h2`
	margin: 0 0 8px;
	color: #303030;
	font-size: 18px;
	font-weight: 600;
	line-height: 1.3;
`;

const Description = styled.p`
	margin: 0 0 24px;
	color: #707070;
	font-size: 12px;
	line-height: 1.45;
`;

const taxQuestionByCountry: Record<string, string> = {
	SG: "Are you registered for goods and services tax (or equivalent) in Singapore?",
	AU: "Are you registered for goods and services tax (or equivalent) in Australia?",
	PH: "Are you registered for Value Added Tax (or equivalent) in the Philippines?",
	ID: "Are you registered for Value Added Tax (or equivalent) in Indonesia?",
	VN: "Are you registered for Value Added Tax (or equivalent) in Vietnam?",
	MY: "Are you registered for Service Tax (or equivalent) in Malaysia?",
	TH: "Are you registered for Value Added Tax (or equivalent) in Thailand?",
};
const taxNumberLabelByCountry: Record<string, string> = {
	SG: "Your GST (registration number)",
	AU: "Your ABN (registration number)",
	PH: "Your TIN (registration number)",
	US: "Your entity's registration number for tax",
	ID: "Your PPN (registration number)",
	VN: "Your Ma so thue (tax code)",
	MY: "Your TIN (registration number)",
	TH: "Your Taxpayer ID",
};
export const OnboardingTaxDetailsStep = ({
	country,
	countryName,
	tax,
	errors,
	config,
	existingNumberLast4,
	taxDirty,
	taxRegistrationChoice,
	agreed = false,
	showAgreement = true,
	onRegistrationChange,
	onTaxChange,
	onAgreementChange = () => undefined,
}: Props) => {
	const [termsOpen, setTermsOpen] = useState(false);
	const taxQuestion = taxQuestionByCountry[country] ?? `Are you registered for tax in ${countryName}?`;
	const taxQuestionCountryStart = taxQuestion.lastIndexOf(" in ") + 4;
	return (
		<>
			<Heading id="creator-onboarding-title-tax">Tax details</Heading>
			<Description>
				Creator Payments International Pte. Ltd. uses these to prepare your Payment Statement.
			</Description>
			{country !== "US" && (
				<TaxQuestion>
					{taxQuestion.slice(0, taxQuestionCountryStart)}
					<strong>{taxQuestion.slice(taxQuestionCountryStart)}</strong>{" "}
					<RequiredMark aria-hidden="true">*</RequiredMark>
				</TaxQuestion>
			)}
			{country !== "US" && (
				<TaxChoices role="radiogroup" aria-label="Tax registration">
					<TaxChoice
						type="button"
						role="radio"
						aria-checked={taxRegistrationChoice === true}
						$selected={taxRegistrationChoice === true}
						onClick={() => {
							onRegistrationChange(true);
						}}
					>
						Yes
					</TaxChoice>
					<TaxChoice
						type="button"
						role="radio"
						aria-checked={taxRegistrationChoice === false}
						$selected={taxRegistrationChoice === false}
						onClick={() => {
							onRegistrationChange(false);
						}}
					>
						No
					</TaxChoice>
				</TaxChoices>
			)}
			{errors.registrationChoice && <TaxError role="alert">{errors.registrationChoice}</TaxError>}
			{(country === "US" || taxRegistrationChoice === true) && (
				<TaxFields>
					<Field
						htmlFor="creator-onboarding-tax-number"
						label={taxNumberLabelByCountry[country] ?? "Your tax registration number"}
						required
						error={errors.registrationNumber}
					>
						<Input
							id="creator-onboarding-tax-number"
							value={tax.registrationNumber}
							autoComplete="off"
							placeholder={
								existingNumberLast4 && !taxDirty ? `On file, ending ${existingNumberLast4}` : ""
							}
							onChange={(event) => onTaxChange("registrationNumber", event.target.value)}
						/>
					</Field>
					<Field htmlFor="creator-onboarding-tax-rate" label="Rate">
						<Input
							id="creator-onboarding-tax-rate"
							value={config?.effectiveRate == null ? "—" : formatRatePercent(config.effectiveRate)}
							readOnly
							aria-label="Current tax rate"
						/>
					</Field>
				</TaxFields>
			)}
			<TaxQuestion $strong>
				You are paid as <RequiredMark aria-hidden="true">*</RequiredMark>
			</TaxQuestion>
			<TaxChoices role="radiogroup" aria-label="You are paid as">
				<TaxChoice
					type="button"
					role="radio"
					aria-checked={tax.entityType === "individual"}
					$selected={tax.entityType === "individual"}
					onClick={() => onTaxChange("entityType", "individual")}
				>
					Individual
				</TaxChoice>
				<TaxChoice
					type="button"
					role="radio"
					aria-checked={tax.entityType === "company"}
					$selected={tax.entityType === "company"}
					onClick={() => onTaxChange("entityType", "company")}
				>
					Company
				</TaxChoice>
			</TaxChoices>
			{errors.entityType && <TaxError role="alert">{errors.entityType}</TaxError>}
			<TaxNotice>
				You are declaring your own tax position. We do not calculate, verify or advise on it. Your declaration
				is pinned to each Payment Statement and cannot be changed retrospectively.
			</TaxNotice>
			{showAgreement && (
				<TermsAgreement>
					<input
						type="checkbox"
						aria-label="Agree to Creator Payments terms"
						checked={agreed}
						onChange={(event) => onAgreementChange(event.target.checked)}
					/>
					<span>
						I agree to the{" "}
						<button type="button" onClick={() => setTermsOpen(true)}>
							{PAYMENT_TERMS_NAME}
						</button>
					</span>
				</TermsAgreement>
			)}
			<CreatorPaymentTermsModal
				open={termsOpen}
				onClose={() => setTermsOpen(false)}
				onAccept={() => {
					onAgreementChange(true);
					setTermsOpen(false);
				}}
			/>
		</>
	);
};
